import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import AuthenticatedLayout from "../../components/Layout/authenticatedLayout";
import ImportsDataManager from "../../components/DataManagers/ImportsDataManager";
import RaceDataDataManager from "../../components/DataManagers/RaceDataDataManager";
import RaceTable from "../../components/Race/RaceTable";
import { useSelector } from "react-redux";
import { RootState } from "../../state";
import { isEqual } from "lodash";
import toast from "react-hot-toast";
import {
  getDefaultDriverPreference,
  getRightGraphCountPreference,
  setRightGraphCountPreference,
  getLeftGraphCountPreference,
  setLeftGraphCountPreference,
} from "../../utils/preferences";
import LapControls from "../../components/Race/LapControls";
import AttackControls from "../../components/Race/AttackControls";
import PitControls from "../../components/Race/PitControls";
import PitBoostSocControls from "../../components/Race/PitBoostSocControls";
import GapsTable from "../../components/Race/WidgetTables/GapsTable";
import TimingDataManager from "../../components/DataManagers/TimingDataManager";
import OffsetPreviewsTable from "../../components/Race/WidgetTables/OffsetPreviewsTable";
import InterventionsTable from "../../components/Race/WidgetTables/InterventionsTable";
import InterventionsDataManager from "../../components/DataManagers/InterventionsDataManager";
import DriverDeltaTable from "../../components/Race/WidgetTables/DriverDeltaTable";
import AttackRemainingTable from "../../components/Race/WidgetTables/AttackRemainingTable";
import Caterpillar from "../../components/Race/Caterpillar";
import PitWindowBars from "../../components/Race/PitWindowBars";
import TimeRemainingTable from "../../components/Race/WidgetTables/TimeRemainingTable";
import GraphSwitcher from "../../components/Race/GraphSwitcher";
import TrackMapDataManager from "../../components/DataManagers/TrackMapDataManager";
import VirtualGapsDataManager from "../../components/DataManagers/VirtualGapsDataManager";
import DriverGPSDataManager from "../../components/DataManagers/DriverGPSDataManager";
import PitPreviewsTable from "../../components/Race/WidgetTables/PitPreviewsTable";
import OptimisationTriggerTable from "../../components/Race/OptimisationTriggerTable";
import AttackModeDataManager from "../../components/DataManagers/AttackModeDataManager";
import { numericalString } from "../../types";

function RacePage() {
  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );

  const [selectedCar, setSelectedCar] = useState<string>(
    () => getDefaultDriverPreference() ?? Object.keys(jagDrivers)[0] ?? "",
  );

  const [rightGraphCount, setRightGraphCount] = useState<number>(() =>
    getRightGraphCountPreference(),
  );

  const handleRightGraphCountChange = (count: number) => {
    setRightGraphCount(count);
    setRightGraphCountPreference(count);
  };

  const [leftGraphCount, setLeftGraphCount] = useState<number>(() =>
    getLeftGraphCountPreference(),
  );

  const handleLeftGraphCountChange = (count: number) => {
    setLeftGraphCount(count);
    setLeftGraphCountPreference(count);
  };

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const isPitBoostRace = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.BPitBoostRace === 1,
    isEqual,
  );

  const variablePitBoostEnabled = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.BEnableVariablePitBoost === 1,
    isEqual,
  );

  const nAttackActivations = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data?.NAttackActivations ?? 0,
    isEqual,
  );

  const carOptions = React.useMemo(() => {
    return Object.entries(jagDrivers)
      .filter(([, driverId]) => driverId)
      .map(([carKey, driverId]) => {
        const driver = driverId ? driverInfo[driverId] : undefined;
        const colour = driver?.driverHighlightColourAlt || "#888";
        return {
          value: carKey,
          label: driver?.driverName || "",
          colour,
        };
      });
  }, [jagDrivers, driverInfo]);

  const selectedCarNumber = React.useMemo(() => {
    const driverId = jagDrivers[selectedCar];
    return driverId ? driverInfo[driverId]?.driverNumber : undefined;
  }, [jagDrivers, driverInfo, selectedCar]);

  const raceControlMessages = useSelector(
    (state: RootState) => state.aks.v2.raceControl?.messages,
    isEqual,
  );

  const seenRaceControlKeys = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!raceControlMessages) return;

    const keys = Object.keys(raceControlMessages);

    if (seenRaceControlKeys.current === null) {
      seenRaceControlKeys.current = new Set(keys);
      return;
    }

    for (const key of keys) {
      if (seenRaceControlKeys.current.has(key)) continue;
      seenRaceControlKeys.current.add(key);

      const message = raceControlMessages[key as numericalString];
      if (message?.text) {
        toast(message.text, { icon: "❗", position: "bottom-center" });
      }
    }
  }, [raceControlMessages]);

  return (
    <AuthenticatedLayout title={"Race"} showJaguarLogo={false}>
      <ImportsDataManager />
      <TimingDataManager />
      <TrackMapDataManager />
      <RaceDataDataManager />
      <VirtualGapsDataManager />
      <InterventionsDataManager />
      <DriverGPSDataManager />
      <AttackModeDataManager />
      <div className={styles.container}>
        <div className={`${styles.column} ${styles.leftColumnGroupContainer}`}>
          <div className={styles.widget}>
            {carOptions.map((car) => (
              <label key={car.value} className={styles.carOption}>
                <input
                  type="radio"
                  name="carColour"
                  value={car.value}
                  checked={selectedCar === car.value}
                  onChange={() => setSelectedCar(car.value)}
                />
                <span style={{ color: car.colour }}>{car.label}</span>
              </label>
            ))}
          </div>
          <div className={styles.leftColumnGroup}>
            <div className={`${styles.column} ${styles.controlsColumn}`}>
              <LapControls carColour={selectedCar} />
              {nAttackActivations > 1 && (
                <AttackControls carColour={selectedCar} />
              )}
              {variablePitBoostEnabled && isPitBoostRace && (
                <PitControls carColour={selectedCar} />
              )}
              {isPitBoostRace && (
                <PitBoostSocControls
                  key={selectedCar}
                  carColour={selectedCar}
                />
              )}
              <OptimisationTriggerTable carColour={selectedCar} />
              <div className={styles.leftGraphsSection}>
                <div className={styles.widget}>
                  <select
                    className={styles.selector}
                    value={leftGraphCount}
                    onChange={(e) =>
                      handleLeftGraphCountChange(Number(e.target.value))
                    }
                  >
                    <option value={1}>1 Graph</option>
                    <option value={2}>2 Graphs</option>
                    <option value={3}>3 Graphs</option>
                  </select>
                </div>
                <div
                  className={styles.leftGraphsGrid}
                  style={{ gridTemplateRows: `repeat(${leftGraphCount}, 1fr)` }}
                >
                  {Array.from({ length: leftGraphCount }).map((_, i) => (
                    <GraphSwitcher
                      key={`left-${leftGraphCount}-${i + 1}`}
                      id={4 + i}
                      carColour={selectedCar}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className={`${styles.column} ${styles.widgetsColumn}`}>
              <OffsetPreviewsTable carColour={selectedCar} />
              {variablePitBoostEnabled && isPitBoostRace && (
                <PitPreviewsTable carColour={selectedCar} />
              )}
              <GapsTable carNumber={selectedCarNumber} />
              <InterventionsTable />
              <DriverDeltaTable carColour={selectedCar} />
              <AttackRemainingTable carNumber={selectedCarNumber} />
              <TimeRemainingTable
                carColour={selectedCar}
                carNumber={selectedCarNumber}
              />
              <Caterpillar carColour={selectedCar} />
              {isPitBoostRace && (
                <PitWindowBars
                  carColour={selectedCar}
                  carNumber={selectedCarNumber}
                />
              )}
            </div>
          </div>
        </div>
        <div className={styles.raceTable}>
          <RaceTable carColour={selectedCar} />
        </div>
        <div className={`${styles.column} ${styles.graphsColumn}`}>
          <div className={styles.widget}>
            <select
              className={styles.selector}
              value={rightGraphCount}
              onChange={(e) =>
                handleRightGraphCountChange(Number(e.target.value))
              }
            >
              <option value={1}>1 Graph</option>
              <option value={2}>2 Graphs</option>
              <option value={3}>3 Graphs</option>
            </select>
          </div>
          <div
            className={styles.graphsGrid}
            style={{ gridTemplateRows: `repeat(${rightGraphCount}, 1fr)` }}
          >
            {Array.from({ length: rightGraphCount }).map((_, i) => (
              <GraphSwitcher
                key={`${rightGraphCount}-${i + 1}`}
                id={i + 1}
                carColour={selectedCar}
              />
            ))}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default RacePage;
